<script>
import DashboardIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

export const CONVERSATION_TAB = 'conversation';
export const CONTACT_TAB = 'contact';

/**
 * Alternador entre os detalhes da CONVERSA e os do CONTATO.
 *
 * Os dois botões são grandes e carregam ícone + rótulo de propósito: o critério
 * de aceite da #1243 é o cliente saber, só olhando, qual aba leva a quê.
 *
 * O fundo da aba ativa é um "polegar" absoluto que desliza de uma aba para a
 * outra, e não a cor de fundo do próprio botão — é o mesmo mecanismo do
 * `SegmentedControl` das configurações de perfil (`components/widgets`). Pintar
 * o botão não animaria nada: `background-color` troca de um lado para o outro
 * sem passar pelo meio. Ele não é reaproveitado aqui porque aquele componente é
 * `radiogroup`, sem `aria-controls`, sem `tabindex` móvel e sem ícone.
 *
 * A navegação segue o padrão de `tablist` do ARIA: só a aba ativa é alcançável
 * por Tab (`tabindex` móvel), e as setas trocam de aba. Sem isso o Tab pararia
 * duas vezes antes do conteúdo, e quem navega por teclado não teria como trocar
 * de aba sem o mouse.
 */
export default {
  components: {
    DashboardIcon,
  },
  props: {
    active: {
      type: [String, Number],
      required: true,
    },
    // Lista de abas de quem usa o componente fora do painel da conversa
    // (#1652: a tela de contato). Cada item: `{ key, label, icon? }`. Sem ela,
    // valem as duas abas fixas do painel da conversa -- o comportamento de
    // antes, inclusive a ordem. A lista pode encolher (permissao derrubando
    // uma aba): o polegar e as setas se guiam so pelo que vier aqui.
    items: {
      type: Array,
      default: null,
    },
    // Prefixo dos ids de aba e de painel (`<prefixo>-tab-<chave>`). Duas telas
    // com o mesmo prefixo repetiriam ids no DOM.
    idPrefix: {
      type: String,
      default: 'conversation-panel',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },
  computed: {
    activeIndex() {
      const index = this.tabs.findIndex(tab => this.isActive(tab.key));
      return index === -1 ? 0 : index;
    },
    // A camada do polegar vale exatamente a área interna do trilho, então aqui
    // basta uma fração dela: nada nesta conta precisa saber quanto vale o
    // padding, que nem segue a escala do Tailwind neste projeto.
    thumbStyle() {
      const count = this.tabs.length || 1;

      // O sinal do deslocamento vem do CSS (`--panel-tabs-thumb-dir`), não
      // daqui: o polegar está em fluxo dentro do trilho, então encosta na
      // borda INICIAL — a esquerda em LTR, a direita em RTL — e `translateX`
      // positivo empurra para a direita nos dois casos. Sem a variável, um
      // locale RTL (`ar`/`fa`/`he`/`ku`/`ur`, ver `App.vue`) pintava a aba
      // errada e deslizava para o lado oposto.
      return {
        width: `${100 / count}%`,
        transform: `translateX(calc(${this.activeIndex} * 100% * var(--panel-tabs-thumb-dir, 1)))`,
      };
    },
    // O CONTATO vem primeiro. A ordem daqui e a unica fonte da posicao: o
    // polegar se guia pelo indice do array, e as setas do teclado andam na
    // mesma sequencia -- nao ha nada no CSS para acompanhar a troca.
    tabs() {
      if (this.items) {
        return this.items;
      }

      return [
        {
          key: CONTACT_TAB,
          icon: 'person-outline',
          label: this.$t('CONVERSATION_SIDEBAR.PANEL_TABS.CONTACT'),
        },
        {
          key: CONVERSATION_TAB,
          icon: 'chat-outline',
          label: this.$t('CONVERSATION_SIDEBAR.PANEL_TABS.CONVERSATION'),
        },
      ];
    },
  },
  methods: {
    isActive(key) {
      return this.active === key;
    },
    tabId(key) {
      return `${this.idPrefix}-tab-${key}`;
    },
    panelId(key) {
      return `${this.idPrefix}-tabpanel-${key}`;
    },
    onSelect(key) {
      if (this.isActive(key)) {
        return;
      }

      this.$emit('change', key);
    },
    // `step` e +1 para a seta da direita e -1 para a da esquerda, e a conta
    // da volta no fim da lista. Com duas abas as duas setas levam a mesma
    // aba, que e o que o painel da conversa sempre fez. Em RTL a seta da
    // esquerda avanca, como em qualquer tablist.
    onArrowKey(step) {
      const count = this.tabs.length;
      if (count < 2) {
        return;
      }

      const isRtl = window.getComputedStyle(this.$el).direction === 'rtl';
      const delta = isRtl ? -step : step;
      const next = this.tabs[(this.activeIndex + delta + count) % count].key;
      this.$emit('change', next);
      this.$nextTick(() => {
        const button = this.$refs[this.tabId(next)];
        // Vem ARRAY mesmo com nome próprio por aba: o Vue 2 marca `refInFor`
        // em qualquer ref declarado dentro de `v-for`, e o `registerRef`
        // sempre grava lista. Quem "simplificar" para `button.focus()` quebra
        // o foco das setas sem nada ficar vermelho na suíte.
        if (button && button[0]) {
          button[0].focus();
        }
      });
    },
  },
};
</script>

<template>
  <div
    role="tablist"
    :aria-label="ariaLabel || $t('CONVERSATION_SIDEBAR.PANEL_TABS.ARIA_LABEL')"
    class="relative grid grid-flow-col auto-cols-fr items-center p-0.5 rounded-full bg-surface-sunken dark:bg-surface-raised border border-line"
  >
    <!-- Colunas iguais, e não `flex-1`: item flex não encolhe abaixo do próprio
         texto, então um rótulo mais longo deixaria as duas metades desiguais e
         o polegar — que mede metade do trilho — não casaria com nenhuma delas.

         A camada abaixo cobre exatamente a área interna (`inset-0.5` espelha o
         `p-0.5`), e é por isso que o polegar pode medir `100% / n` sem saber
         quanto vale o padding. -->
    <span aria-hidden="true" class="absolute pointer-events-none inset-0.5">
      <span
        class="block h-full rounded-full panel-tabs__thumb bg-brand-solid"
        :style="thumbStyle"
      />
    </span>
    <button
      v-for="tab in tabs"
      :id="tabId(tab.key)"
      :key="tab.key"
      :ref="tabId(tab.key)"
      type="button"
      role="tab"
      :aria-selected="isActive(tab.key).toString()"
      :aria-controls="panelId(tab.key)"
      :tabindex="isActive(tab.key) ? 0 : -1"
      class="relative z-10 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors duration-150"
      :class="
        isActive(tab.key) ? 'text-on-brand' : 'text-muted hover:text-body'
      "
      @click="onSelect(tab.key)"
      @keydown.left.prevent="onArrowKey(-1)"
      @keydown.right.prevent="onArrowKey(1)"
    >
      <DashboardIcon v-if="tab.icon" :icon="tab.icon" size="14" />
      <span class="truncate">{{ tab.label }}</span>
    </button>
  </div>
</template>

<style scoped>
.panel-tabs__thumb {
  transition: transform 200ms ease-out;
}

[dir='rtl'] .panel-tabs__thumb {
  --panel-tabs-thumb-dir: -1;
}

@media (prefers-reduced-motion: reduce) {
  .panel-tabs__thumb {
    transition: none;
  }
}
</style>
